import { graphql, useFragment } from "react-relay";

import { useHomeV2StoryPackageTopic } from "scmp-app/components/home-v2/secondary-story-package/hooks";
import type { entityLink$key } from "scmp-app/queries/__generated__/entityLink.graphql";
import type { fallbackQueryLoaderQuery$data } from "scmp-app/queries/__generated__/fallbackQueryLoaderQuery.graphql";
import type { queueTopicHooksHomepageMidPageQueueTopicQuery$key } from "scmp-app/queries/__generated__/queueTopicHooksHomepageMidPageQueueTopicQuery.graphql";

/**
 * SCMPWEB-9641: topic (custom_text / refTerm) + content edges for the two
 * mid-page story-package queues, read off the fallback query response.
 *
 * The queue edges are read directly off the query response fields
 * (`packageTopQueue` / `storyPackageTwoQueue`), which the fallback query
 * fetches each under its own `@include` flag — a slot left on AI by the
 * app-config is excluded from the fetch, so its queue field is absent
 * (`undefined`) on the response. Optional chaining degrades that to empty
 * edges / no topic instead of tripping Relay's "fragment reference was not
 * found" invariant (SCMPWEB-9641).
 */
export const useHomepageMidPageQueueTopic = (fallbackData: fallbackQueryLoaderQuery$data) => {
  const queueTopics = useFragment(
    graphql`
      fragment queueTopicHooksHomepageMidPageQueueTopicQuery on Query
      @argumentDefinitions(
        packageTopQueueName: { type: "String!" }
        storyPackageTwoQueueName: { type: "String!" }
      ) {
        packageTopQueue: queue(filter: { name: $packageTopQueueName }) {
          ...hooksHomeV2StoryPackageTopicQueue
        }
        storyPackageTwoQueue: queue(filter: { name: $storyPackageTwoQueueName }) {
          ...hooksHomeV2StoryPackageTopicQueue
        }
      }
    `,
    fallbackData as queueTopicHooksHomepageMidPageQueueTopicQuery$key,
  );

  const packageTopTopic = useHomeV2StoryPackageTopic(queueTopics?.packageTopQueue);
  const storyPackageTwoTopic = useHomeV2StoryPackageTopic(queueTopics?.storyPackageTwoQueue);

  return {
    packageTopEdges: fallbackData.packageTopQueue?.items?.edges ?? [],
    packageTopTopicName: packageTopTopic.name || undefined,
    packageTopTopicReference: (packageTopTopic.term ?? null) as entityLink$key | null,
    storyPackageTwoEdges: fallbackData.storyPackageTwoQueue?.items?.edges ?? [],
    storyPackageTwoTopicName: storyPackageTwoTopic.name || undefined,
    storyPackageTwoTopicReference: (storyPackageTwoTopic.term ?? null) as entityLink$key | null,
  };
};
